import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, Stack, Typography, Button, CircularProgress, Divider,
} from '@mui/material';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { Pathname } from 'Routes';
import { isMobileDevice, buildAppSchemeLink } from 'Utils/appDeepLink';
import { V2ThemeProvider } from '../../../private/v2/_shared/V2Layout';

/**
 * TV login CONSENT screen — /tv-login-approve?code=310378
 *
 * A TV login link used to link the device the instant it was opened, which
 * means anyone who got hold of the link (or a mistyped/forwarded one) could
 * attach a TV to the signed-in account with no confirmation. This page asks
 * first: nothing is sent until "Allow" is pressed, and "Decline" just walks
 * back to wherever the user came from.
 *
 * Signed out? The path (code included) is parked in `storedPath` and the user
 * is sent to sign-in — the sign-in flow navigates back here afterwards, so the
 * same Allow/Decline prompt is shown once there's a session.
 *
 * ON A PHONE the code is also offered to the native app, which owns its own
 * session and shows its own Allow/Decline prompt. That attempt uses the custom
 * scheme (projector://tvlogin?code=…), because iOS Safari opens an https
 * universal link ONLY on a genuine tap of an <a href> — a JS navigation to one
 * is ignored, chottu's page loads in Safari instead, and the user is dumped in
 * the App Store even with the app installed.
 *
 * iOS ALWAYS confirms a scheme hand-off with its own "Open in Projector?"
 * prompt, and that prompt sits on top of this page. Showing the Allow/Decline
 * card underneath it invites the user to ignore the prompt and approve on the
 * web instead, so the card stays hidden behind a neutral screen until we know
 * the hand-off didn't happen: the page going hidden means the app took over,
 * and still being visible a moment later means it didn't (no app, or the user
 * dismissed the prompt). Whether an app is installed cannot be detected up
 * front, so this after-the-fact check is the only signal available.
 */
const TV_APP_DEEPLINK_PATH = 'tvlogin'; // → projector://tvlogin?code=…
const handoffKey = (code) => `tvAppHandoff:${code}`;
// Backstop only. The card is normally revealed by the user's first tap, which
// can't happen until iOS's dialog is gone — a purely time-based reveal would
// paint the Allow/Decline buttons BEHIND the dialog, which is the whole thing
// we're avoiding. This just stops someone staring at a spinner forever.
const HANDOFF_SETTLE_MS = 8000;

const TVLoginApprove = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const code = (searchParams.get('code') || '').trim();
  const { setAppSnackbar } = useContext(AppContext);

  // pending = waiting on the user, linking = request in flight
  const [status, setStatus] = useState('pending');
  const redirectedRef = useRef(false);
  const handoffRef = useRef(false);

  // Should this visit try the app at all? Decided on the first render so the
  // card is never painted for a phone that is about to hand off.
  const [handingOff, setHandingOff] = useState(() => {
    if (!code) return false;
    if (searchParams.get('noapp') === '1' || !isMobileDevice()) return false;
    try {
      if (sessionStorage.getItem(handoffKey(code))) return false;
    } catch (e) { /* private mode — attempt anyway */ }
    return true;
  });

  // Offer the code to the app, once per code per tab, and keep the card hidden
  // until iOS's prompt has been dealt with one way or the other.
  useEffect(() => {
    if (!handingOff || handoffRef.current) return undefined;
    handoffRef.current = true;
    try { sessionStorage.setItem(handoffKey(code), '1'); } catch (e) { /* ignore */ }
    window.location.href = buildAppSchemeLink(TV_APP_DEEPLINK_PATH, { code });

    const reveal = () => { setHandingOff(false); };
    let timer = setTimeout(reveal, HANDOFF_SETTLE_MS);

    // A tap can only reach the page once iOS's dialog has been dismissed, so
    // it's the one reliable "the hand-off is over" signal. Cancel → the user
    // taps → card. It also covers the no-app case, where iOS complains that
    // the address is invalid and the user has to dismiss that alert first.
    const onTap = () => { clearTimeout(timer); reveal(); };

    // Hidden → the app took over, so hold the neutral screen (nothing should
    // flash behind the app as it opens). Visible again → they came back from
    // the app; give them the card shortly after.
    const onVisibility = () => {
      clearTimeout(timer);
      if (!document.hidden) timer = setTimeout(reveal, 400);
    };

    document.addEventListener('pointerdown', onTap);
    document.addEventListener('touchend', onTap);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onTap);
      document.removeEventListener('touchend', onTap);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [handingOff, code]);

  useEffect(() => {
    // Don't push a logged-out phone through web sign-in while the app is
    // being offered the code — the app carries its own session.
    if (handingOff) return;
    if (redirectedRef.current) return;

    // No code to approve — nothing to consent to. Signed in, fall back to the
    // manual code page; signed out, there's nothing to approve at all.
    if (!code) {
      redirectedRef.current = true;
      navigate(
        localStorage.getItem('token') ? Pathname.tvLogin : Pathname.authentication.newsignIn,
        { replace: true }
      );
      return;
    }

    // Signed out: remember where we were headed and go log in first.
    if (!localStorage.getItem('token')) {
      redirectedRef.current = true;
      const targetPath = `${Pathname.tvLoginApprove}?code=${encodeURIComponent(code)}`;
      localStorage.setItem('storedPath', targetPath);
      navigate(Pathname.authentication.newsignIn, { replace: true });
    }
  }, [code, handingOff]);

  const allow = async () => {
    setStatus('linking');
    try {
      const { success, message } = await dataFetcher(endpoints.registerTv, { code });
      if (success) {
        setStatus('success');
      } else {
        setStatus('failed');
        setAppSnackbar?.({
          isVisible: true,
          type: 'error',
          message: message || 'That code didn\u2019t work. Try again.',
        });
      }
    } catch (e) {
      setStatus('failed');
      setAppSnackbar?.({
        isVisible: true,
        type: 'error',
        message: 'Something went wrong linking the device.',
      });
    }
  };

  // Leave this screen and go back to whatever the user was doing — used by
  // Decline, by Cancel and by Close once the TV is linked. The history entry
  // for the link itself is `replace`d away by the redirects that land here, so
  // -1 is the page before the TV link. With no history to go back to (link
  // opened in a fresh tab), fall back to the dashboard. Mirrors the app, where
  // this screen is pushed on top of whatever the user was doing and dismisses
  // back to it rather than navigating somewhere new.
  const closeScreen = () => {
    const hasHistory = window.history.length > 1 && location.key !== 'default';
    if (hasHistory) navigate(-1);
    else navigate(Pathname.dashboard, { replace: true });
  };

  if (!code) return null;

  // Neutral holding screen: shown only while iOS's "Open in Projector?" prompt
  // is up, so the Allow/Decline buttons aren't sitting there as a competing
  // option. Replaced by the card the moment we know the app didn't take over.
  if (handingOff) {
    return (
      <V2ThemeProvider>
        <Box sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
        >
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <CircularProgress size={26} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Opening the Projector app…
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 260 }}>
              Didn’t open? Tap anywhere to approve here instead.
            </Typography>
          </Stack>
        </Box>
      </V2ThemeProvider>
    );
  }

  return (
    <V2ThemeProvider>
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
      >
        <Card sx={{
          p: { xs: 3, md: 4 }, maxWidth: 460, width: '100%', textAlign: 'center',
        }}
        >
          {(status === 'pending' || status === 'linking') && (
            <>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              >
                <TvRoundedIcon sx={{ fontSize: 34 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.75 }}>
                Allow this TV to sign in?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                A device is asking to sign in to your Projector account using the
                code below. Only allow it if you started this sign-in on your own
                TV or streaming device.
              </Typography>

              <Box sx={{
                py: 1.5,
                px: 2,
                mb: 2.5,
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
              >
                <Typography sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
                >
                  TV code
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: 6 }}>
                  {code}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                sx={{ justifyContent: 'center' }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  size="large"
                  disabled={status === 'linking'}
                  onClick={closeScreen}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Decline
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={status === 'linking'}
                  onClick={allow}
                  startIcon={status === 'linking' ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  {status === 'linking' ? 'Allowing…' : 'Allow'}
                </Button>
              </Stack>

              <Divider sx={{ my: 2.5 }} />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <ShieldRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  If you didn’t ask for this, decline — nothing is shared.
                </Typography>
              </Stack>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleRoundedIcon sx={{ fontSize: 60, color: 'success.main', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>TV login successful</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Your device has been added.
              </Typography>
              <Button
                variant="contained"
                onClick={closeScreen}
                sx={{ borderRadius: 2, fontWeight: 700, width: { xs: '100%', sm: 'auto' }, minWidth: 160 }}
              >
                Close
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 60, color: 'error.main', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>TV login failed</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                We couldn’t add your device. Try entering the code manually.
              </Typography>
              {/* fullWidth + stretch, same as the Allow/Decline row: without it
                  the stacked buttons shrink to their label width and hug the
                  left edge of the card on a phone. */}
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                sx={{ justifyContent: 'center', alignItems: 'stretch' }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={closeScreen}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(Pathname.tvLogin, { replace: true })}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Enter code manually
                </Button>
              </Stack>
            </>
          )}
        </Card>
      </Box>
    </V2ThemeProvider>
  );
};

export default TVLoginApprove;
